// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { DetailPageHeader } from '@/components/detail-page-header';
import { DetailPageHeaderField } from '@/components/detail-page-header-field';
import { Link } from '@/components/link';
import { Shipment, ShipmentType } from '@/shipment/api/types';
import { MissingShipmentChip } from '@/shipment/components/missing-shipment-chip';
import { ShipmentHeaderActions } from '@/shipment/components/shipment-header-actions';
import { ShipmentStatusChip } from '@/shipment/components/shipment-status-chip';
import { Chip, Tooltip } from '@mui/material';

interface Props {
  shipment: Shipment;
  shipmentType: ShipmentType;
  onEditShipment: () => void;
  onMarkShipped?: () => void;
  onMarkDelivered: () => void;
  onMarkReceived?: () => void;
  onCancel: () => void;
}

/**
 * Header section for shipment detail page.
 * Includes breadcrumbs, title, status chip, type, and action buttons.
 */
export function ShipmentDetailHeader(props: Props) {
  return (
    <DetailPageHeader
      parentBreadcrumb={
        <Link
          to="/shipments"
          search={{ type: props.shipmentType }}
          variant="body2"
          color="text.secondary"
          sx={{ display: 'block' }}
        >
          Shipments
        </Link>
      }
      currentBreadcrumb={props.shipment.shipmentNumber}
      title={props.shipment.shipmentNumber}
      titleAdornment={
        <>
          <ShipmentStatusChip status={props.shipment.status} />
          {props.shipment.thirdPartyBound && (
            <Tooltip arrow title="This shipment came from your 3PL">
              <Chip label="3PL" size="small" />
            </Tooltip>
          )}
          <MissingShipmentChip
            missingSince={props.shipment.missingSince}
            thirdPartyBound={props.shipment.thirdPartyBound}
          />
        </>
      }
      actions={
        <ShipmentHeaderActions
          shipment={props.shipment}
          onEditShipment={props.onEditShipment}
          onMarkShipped={props.onMarkShipped}
          onMarkDelivered={props.onMarkDelivered}
          onMarkReceived={props.onMarkReceived}
          onCancel={props.onCancel}
        />
      }
    >
      <DetailPageHeaderField label="Type">
        {props.shipmentType === 'inbound' ? 'Inbound' : 'Outbound'}
      </DetailPageHeaderField>

      {props.shipment.fulfillmentOrder && (
        <DetailPageHeaderField label="Fulfillment order">
          <Link
            to="/fulfillments/$fulfillmentKey"
            params={{
              fulfillmentKey: props.shipment.fulfillmentOrder.key,
            }}
            variant="body2"
          >
            {props.shipment.fulfillmentOrder.fulfillmentOrderNumber}
          </Link>
        </DetailPageHeaderField>
      )}
      {props.shipment.purchaseOrder && (
        <DetailPageHeaderField label="Purchase order">
          <Link
            to="/purchase-orders/$purchaseOrderKey"
            params={{
              purchaseOrderKey: props.shipment.purchaseOrder.key,
            }}
            variant="body2"
          >
            {props.shipment.purchaseOrder.purchaseOrderNumber}
          </Link>
        </DetailPageHeaderField>
      )}
      {props.shipment.returnOrder && (
        <DetailPageHeaderField label="Return order">
          <Link
            to="/return-orders/$returnOrderKey"
            params={{
              returnOrderKey: props.shipment.returnOrder.key,
            }}
            variant="body2"
          >
            {props.shipment.returnOrder.returnOrderNumber}
          </Link>
        </DetailPageHeaderField>
      )}
    </DetailPageHeader>
  );
}
